import { useMemo } from "react";
import { Box, Tabs, Tab, Typography, Badge, alpha, useTheme } from "@mui/material";
import { ACCENT } from "../accent";

/**
 * Tab bar over the log's analysis modules.
 *
 * Each tab carries the worst severity its module found as a dot and the count
 * beside it, so the page says where to look before anything is opened. A module
 * the backend could not run stays selectable — its panel explains why, which is
 * more useful than a tab that silently disappears.
 */
const SEVERITY_COLOUR = (theme) => ({
  critical: theme.palette.error.main,
  warn: theme.palette.warning.main,
  info: theme.palette.info.main,
  good: theme.palette.success.main,
});

const RANK = { critical: 0, warn: 1, info: 2, good: 3 };

export const worstSeverity = (findings = []) =>
  findings.length === 0
    ? null
    : findings.reduce((worst, f) => (RANK[f.severity] < RANK[worst] ? f.severity : worst), "good");

const AnalysisTabs = ({ tabs, value, onChange, analysis }) => {
  const theme = useTheme();
  const colours = SEVERITY_COLOUR(theme);

  const status = useMemo(() => {
    const modules = analysis?.modules || {};
    return Object.fromEntries(
      Object.entries(modules).map(([name, m]) => [
        name,
        {
          severity: worstSeverity(m.findings),
          count: (m.findings || []).filter((f) => f.severity !== "good").length,
          unavailable: m.status !== "ok",
        },
      ])
    );
  }, [analysis]);

  return (
    <Box sx={{ borderBottom: `1px solid ${alpha(theme.palette.divider, 0.6)}`, mb: 3 }}>
      <Tabs
        value={value}
        onChange={(e, next) => onChange(next)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{
          minHeight: 44,
          "& .MuiTabs-indicator": { backgroundColor: ACCENT },
          "& .MuiTab-root": { minHeight: 44, textTransform: "none", fontSize: 14 },
          "& .Mui-selected": { color: `${ACCENT} !important` },
        }}
      >
        {tabs.map((tab) => {
          const state = tab.module ? status[tab.module] : null;
          return (
            <Tab
              key={tab.id}
              value={tab.id}
              disableRipple
              label={
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                  <Typography
                    variant="body2"
                    sx={{ color: state?.unavailable ? theme.palette.text.disabled : "inherit" }}
                  >
                    {tab.label}
                  </Typography>
                  {state?.severity && (
                    <Box
                      component="span"
                      sx={{ width: 7, height: 7, borderRadius: "50%", backgroundColor: colours[state.severity] }}
                    />
                  )}
                  {state?.count > 0 && (
                    <Badge
                      badgeContent={state.count}
                      sx={{
                        "& .MuiBadge-badge": {
                          position: "static",
                          transform: "none",
                          fontSize: 10,
                          height: 16,
                          minWidth: 16,
                          backgroundColor: alpha(theme.palette.text.primary, 0.12),
                          color: theme.palette.text.secondary,
                        },
                      }}
                    />
                  )}
                </Box>
              }
            />
          );
        })}
      </Tabs>
    </Box>
  );
};

export default AnalysisTabs;
